import type { Permission } from 'paintscout'
import { getHighestRole } from '../getHighestRole'
import { PAINTER_EMPLOYEE_PERMISSIONS } from './painterAccounts'

export type UserSeatType = 'core' | 'team' | 'painter'
export type PainterRole = 'employee' | 'subcontractor'

export const USER_SEAT_LABELS: Record<UserSeatType, string> = {
  core: 'Sales',
  team: 'Team',
  painter: 'Painter'
}

export const PAINTER_ROLE_LABELS: Record<PainterRole, string> = {
  employee: 'Employee',
  subcontractor: 'Subcontractor'
}

export const USER_SEAT_COLOR_TOKENS: Record<UserSeatType, { dark: string; background: string }> = {
  core: { dark: 'brand.Blue.Blue55', background: 'secondary.260.C95' },
  team: { dark: 'brand.Neutral.P50', background: 'brand.Neutral.P94' },
  painter: { dark: 'secondary.200.C70', background: 'secondary.200.C95' }
}

export function getSeatTypeFromRoles(roles: string[] = []): UserSeatType | undefined {
  const highestRole = getHighestRole(roles)
  if (highestRole === 'core' || highestRole === 'admin' || highestRole === 'sales') return 'core'
  if (highestRole === 'team' || highestRole === 'viewer') return 'team'
  if (highestRole === 'painter') return 'painter'
  return undefined
}

const NEW_STYLE_ROLES = ['core', 'team', 'painter']

export function getEffectiveSeatType(args: { seatType?: string; roles?: string[] }): UserSeatType | undefined {
  const roles = args.roles ?? []
  // For new-style roles (core/team/painter), role is the source of truth
  if (roles.some((r) => NEW_STYLE_ROLES.includes(r))) {
    return getSeatTypeFromRoles(roles)
  }
  // For legacy users, prefer explicit seatType if set
  if (args.seatType === 'core' || args.seatType === 'team' || args.seatType === 'painter') {
    return args.seatType
  }
  // Fall back to detecting from legacy roles (e.g. viewer → team, sales → core)
  return getSeatTypeFromRoles(roles)
}

export function getDefaultSeatPermissions(seatType?: UserSeatType, painterRole?: PainterRole): Permission[] {
  if (seatType === 'painter' && painterRole === 'employee') {
    return [...PAINTER_EMPLOYEE_PERMISSIONS]
  }
  if (seatType === 'painter') return []
  return []
}

export function getDefaultRolesForSeatType(seatType: UserSeatType, currentRoles: string[] = []): string[] {
  if (seatType === 'core') return currentRoles.includes('admin') ? ['admin'] : ['sales']
  if (seatType === 'team') return ['viewer']
  if (seatType === 'painter') return ['painter']
  return ['viewer']
}

export function canTransferAccountOwnershipTo(args: { seatType?: string; roles?: string[] }): boolean {
  return getEffectiveSeatType(args) === 'core'
}
